// Tenant-Admin/Subscription/SubscriptionPlanCard.jsx
import React from 'react';
import { 
  FiCheck, 
  FiX, 
  FiZap, 
  FiUsers, 
  FiBook, 
  FiShield,
  FiGift,
  FiPercent,
  FiTag,
  FiArrowRight
} from 'react-icons/fi';
import { motion } from 'framer-motion';

const SubscriptionPlanCard = ({ plan, isCurrent, isUpgrade, onSelect }) => {
  const isFree = plan.type === 'free';
  
  // Price math variables
  const displayPrice = plan.actualPrice?.amount || plan.pricing?.yearly?.amount || 0;
  const originalPrice = plan.actualPrice?.originalAmount || plan.pricing?.yearly?.amount || 0;
  const isSpecialPrice = plan.actualPrice?.isSpecialPrice || false;
  const discountPercentage = plan.actualPrice?.discountPercentage || 0;
  const discountReason = plan.actualPrice?.discountReason || '';

  const monthlyPrice = plan.pricing?.monthly?.amount || 0;
  const hasTrial = plan.hasTrial || false;
  const trialDays = plan.trialDays || 0;

  // Tax variables
  const taxInfo = plan.tax || {};
  const hasTax = taxInfo.taxPercentage > 0;
  const taxName = taxInfo.taxName || 'GST';
  const taxPercentage = taxInfo.taxPercentage || 0;
  const isTaxInclusive = taxInfo.isTaxInclusive || false;

  // Features mapping
  const features = plan.features || {};
  const displayedFeatures = Object.keys(features).slice(0, 5).map(key => ({
    key,
    label: key.replace(/([A-Z])/g, ' $1').trim(),
    value: features[key]
  }));

  // Metadata metrics
  const modules = plan.modules || {};
  const moduleCount = Object.values(modules).filter(v => v === true).length;
  
  const roleAccess = plan.roleAccess || {};
  const roleCount = Object.values(roleAccess).filter(v => v === true).length;

  const isPrivate = plan.visibility === 'private';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className={`bg-white rounded-2xl border p-5 flex flex-col justify-between shadow-sm transition-all ${
        isCurrent 
          ? 'border-indigo-600 ring-4 ring-indigo-50' 
          : isUpgrade 
            ? 'border-emerald-200 hover:border-emerald-400' 
            : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div>
        {/* Badges Layout */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <h3 className="text-base font-bold text-slate-900 mr-1">{plan.name}</h3>
          
          {isCurrent && (
            <span className="px-2 py-0.5 text-xs font-bold bg-indigo-50 text-indigo-700 rounded-md">
              Active Now
            </span>
          )}
          {isUpgrade && !isCurrent && (
            <span className="px-2 py-0.5 text-xs font-bold bg-emerald-50 text-emerald-700 rounded-md inline-flex items-center gap-1">
              <FiZap className="w-3 h-3" /> Recommended Upgrade
            </span>
          )}
          {hasTrial && !isCurrent && (
            <span className="px-2 py-0.5 text-xs font-bold bg-blue-50 text-blue-700 rounded-md inline-flex items-center gap-1">
              <FiGift className="w-3 h-3" /> {trialDays}-Day Trial
            </span>
          )}
          {isPrivate && (
            <span className="px-2 py-0.5 text-xs font-bold bg-purple-50 text-purple-700 rounded-md inline-flex items-center gap-1">
              <FiTag className="w-3 h-3" /> Private Offer
            </span>
          )}
        </div>

        {/* Short Description */}
        <p className="text-xs text-slate-500 line-clamp-2 mb-4">{plan.description}</p>

        {/* Dynamic Pricing Section */}
        <div className="mb-4">
          {isFree ? (
            <div className="flex items-baseline">
              <span className="text-2xl font-black text-slate-900">Free</span>
              <span className="text-xs text-slate-400 ml-1">forever</span>
            </div>
          ) : isSpecialPrice ? (
            <div className="space-y-1">
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-emerald-600">₹{displayPrice}</span>
                <span className="text-xs text-slate-400">/ year</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-400 line-through">₹{originalPrice}</span>
                <span className="font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                  {Math.round(discountPercentage)}% off
                </span>
              </div>
              {discountReason && (
                <p className="text-[11px] font-medium text-emerald-600">
                  ⚡ {discountReason}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-black text-slate-900">₹{displayPrice}</span>
                <span className="text-xs text-slate-400">/ year</span>
              </div>
              {monthlyPrice > 0 && (
                <p className="text-[11px] text-slate-400">
                  Equivalent to ₹{monthlyPrice}/month
                </p>
              )}
            </div>
          )}
        </div>

        {/* Tax Information Summary */}
        {hasTax && !isFree && (
          <div className="mb-4 p-2.5 bg-slate-50 rounded-xl border border-slate-100 space-y-1">
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <FiPercent className="w-3 h-3 text-slate-400" />
                {taxName} ({taxPercentage}%)
              </span>
              <span>{isTaxInclusive ? 'Included in price' : 'Added at checkout'}</span>
            </div>
            {!isTaxInclusive && (
              <div className="text-[11px] font-bold text-slate-700 flex justify-between pt-1 border-t border-slate-200/50">
                <span>Total with Tax:</span>
                <span>₹{(displayPrice * (1 + taxPercentage / 100)).toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Feature Checkmarks List */}
        <div className="space-y-2 mb-5 border-t border-slate-50 pt-4">
          {displayedFeatures.map((feature) => (
            <div key={feature.key} className="flex items-start gap-2 text-xs">
              {feature.value > 0 ? (
                <FiCheck className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              ) : (
                <FiX className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
              )}
              <span className={feature.value > 0 ? 'text-slate-700 font-medium' : 'text-slate-400'}>
                {feature.label}: {feature.value === 0 ? 'Unlimited' : feature.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Structural Limits & Final CTA Row */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 text-[11px] font-medium text-slate-400 bg-slate-50 p-2 rounded-lg justify-around">
          <span className="flex items-center gap-1">
            <FiBook className="w-3.5 h-3.5" /> {moduleCount} Modules Included
          </span>
          <span className="text-slate-200">|</span>
          <span className="flex items-center gap-1">
            <FiUsers className="w-3.5 h-3.5" /> {roleCount} Role Tiers
          </span>
        </div>

        <button
          onClick={() => !isCurrent && onSelect(plan)}
          disabled={isCurrent}
          className={`w-full py-2.5 px-4 rounded-xl font-bold text-xs transition flex items-center justify-center gap-2 ${
            isCurrent
              ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
              : isUpgrade
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm active:scale-98'
                : 'bg-slate-900 text-white hover:bg-slate-800 active:scale-98'
          }`}
        >
          <span>{isCurrent ? 'Your Current Plan' : isUpgrade ? 'Upgrade Plan Tier' : 'Choose This Plan'}</span>
          {!isCurrent && <FiArrowRight className="w-3.5 h-3.5" />}
        </button>

        {plan.support && (
          <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-400 font-medium pt-1">
            <FiShield className="w-3 h-3 text-slate-400" />
            <span>Customer Support: {plan.support.level}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SubscriptionPlanCard;